import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Upload,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Award,
} from "lucide-react";
import { FooterComponents } from "./FooterComponent";
import TopBarComponent from "./TopBarComponent";
import SidebarComponent from "./SidbarComponent";
import { getToken } from "../utils/auth";

export default function LocalLoanBursaryApplicationUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({ success: [], failed: [] });
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      if (["xlsx", "xls"].includes(fileExtension)) {
        setFile(selectedFile);
        setError("");
        setResults({ success: [], failed: [] });
      } else {
        setError("Please upload a valid Excel file (.xlsx or .xls)");
        setFile(null);
      }
    }
  };

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          resolve(jsonData);
        } catch (err) {
          reject(new Error("Failed to parse Excel file"));
        }
      };

      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  const sendToBackend = async (data) => {
    const response = await fetch(
      process.env.REACT_APP_BASE_URL +
        "hec/busaryApplication/saveOneApplication",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + getToken(),
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Try to parse as JSON, fallback to text if it fails
    try {
      const text = await response.text();
      // Try to parse as JSON
      return JSON.parse(text);
    } catch (e) {
      // If parsing fails, it's likely plain text, return success
      return { success: true };
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }

    setUploading(true);
    setError("");
    setResults({ success: [], failed: [] });
    setProgress({ current: 0, total: 0 });

    try {
      // Parse Excel file
      const parsedData = await parseExcelFile(file);

      if (!parsedData || parsedData.length === 0) {
        throw new Error("No data found in Excel file");
      }

      setProgress({ current: 0, total: parsedData.length });

      const successRecords = [];
      const failedRecords = [];

      // Process each record individually
      for (let i = 0; i < parsedData.length; i++) {
        const record = parsedData[i];

        try {
          // Send single record as array to match your API format
          await sendToBackend(record);
          successRecords.push({ index: i + 1, data: record });
          setProgress({ current: i + 1, total: parsedData.length });
        } catch (err) {
          failedRecords.push({
            index: i + 1,
            data: record,
            error: err.message,
          });
          setProgress({ current: i + 1, total: parsedData.length });
        }
      }

      setResults({ success: successRecords, failed: failedRecords });

      if (failedRecords.length === 0) {
        setError("");
      } else if (successRecords.length === 0) {
        setError("All records failed to upload");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResults({ success: [], failed: [] });
    setError("");
    setProgress({ current: 0, total: 0 });
  };

  return (
    <div className="bg-slate-100 min-h-screen flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="pt-6 sm:ml-64 flex-1 flex flex-col">
        <div className="p-6 flex-1">
          <div className="max-w-5xl mx-auto">
            {/* Success Message */}
            <div className="text-center mt-8">
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-800 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-xl font-bold  mb-2">
                BURSARY APPLICATION STUDENT LISTS TO UPLOAD
              </h1>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h6 className="text-3xl font-normal text-slate-800 mb-6">
                Please, fine-tune the excel file before you upload it based on
                the format given.
              </h6>

              {/* File Upload Section */}
              <div className="mb-6">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  Select Excel File
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors">
                    <Upload className="w-5 h-5 mr-2 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {file ? file.name : "Choose Excel file (.xlsx, .xls)"}
                    </span>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                  {file && !uploading && (
                    <button
                      onClick={resetUpload}
                      className="px-4 py-3 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing... ({progress.current}/{progress.total})
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Upload and Process
                  </>
                )}
              </button>

              {/* Progress Bar */}
              {uploading && progress.total > 0 && (
                <div className="mt-6">
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-indigo-600 h-full transition-all duration-300"
                      style={{
                        width: `${(progress.current / progress.total) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    {Math.round((progress.current / progress.total) * 100)}%
                    Complete
                  </p>
                </div>
              )}

              {/* Results Summary */}
              {(results.success.length > 0 || results.failed.length > 0) && (
                <div className="mt-8 space-y-4">
                  <h2 className="text-xl font-semibold text-gray-800">
                    Upload Results
                  </h2>

                  {/* Success Summary */}
                  {results.success.length > 0 && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        <div>
                          <p className="font-medium text-green-800">
                            Successfully uploaded {results.success.length}{" "}
                            record(s)
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Failed Summary */}
                  {results.failed.length > 0 && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start">
                        <XCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-red-800 mb-2">
                            Failed to upload {results.failed.length} record(s)
                          </p>
                          <div className="max-h-48 overflow-y-auto space-y-2">
                            {results.failed.map((fail, idx) => (
                              <div
                                key={idx}
                                className="text-sm text-red-700 bg-red-100 p-2 rounded"
                              >
                                <p className="font-medium">
                                  Row {fail.index}: {fail.error}
                                </p>
                                <p className="text-xs mt-1 text-red-600">
                                  {fail.data.firstName} {fail.data.lastName} -{" "}
                                  {fail.data.registrationNumber}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <FooterComponents />
      </div>
    </div>
  );
}
